'use client'

import React, { useState, useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Search, ChevronDown, ArrowRight } from 'lucide-react'
import type { Team, Media } from '@/payload-types'
import { RichText } from '../RichText'
import { getMediaUrl } from '../../lib/media'

const DEPARTMENT_LABELS: Record<string, string> = {
  attorneys: 'Attorneys',
  paralegals: 'Paralegals',
  operations: 'Operations',
  'client-services': 'Client Success',
  'business-development': 'Business Development',
  'canadian-immigration': 'Canadian Immigration',
}

function formatDepartmentLabel(department: string): string {
  return DEPARTMENT_LABELS[department] ?? department.replace(/-/g, ' ')
}

interface OurTeamProps {
  teamMembers: Team[]
  heading?: any
  content?: any
  departmentOrder?: string[]
}

export function OurTeam({ teamMembers, heading, content, departmentOrder }: OurTeamProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTitle, setSelectedTitle] = useState<string>('all')
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all')
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)

  // Extract unique values for filters
  const titles = useMemo(() => {
    const uniqueTitles = [...new Set(teamMembers.map((m) => m.title).filter(Boolean))]
    return uniqueTitles
  }, [teamMembers])

  const departments = useMemo(() => {
    const uniqueDepts = [...new Set(teamMembers.map((m) => m.department).filter(Boolean))]
    return uniqueDepts
  }, [teamMembers])

  // Filter team members
  const filteredMembers = useMemo(() => {
    return teamMembers.filter((member) => {
      const matchesSearch =
        searchQuery === '' ||
        member.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.title?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesTitle = selectedTitle === 'all' || member.title === selectedTitle
      const matchesDepartment =
        selectedDepartment === 'all' || member.department === selectedDepartment

      return matchesSearch && matchesTitle && matchesDepartment
    })
  }, [teamMembers, searchQuery, selectedTitle, selectedDepartment])

  // Group filtered members by department
  const groupedByDepartment = useMemo(() => {
    const map = new Map<string, Team[]>()
    for (const member of filteredMembers) {
      const dept = member.department || 'Other'
      if (!map.has(dept)) {
        map.set(dept, [])
      }
      map.get(dept)!.push(member)
    }
    const groups = Array.from(map.entries()).map(([department, members]) => ({
      department,
      members,
    }))
    if (departmentOrder) {
      groups.sort((a, b) => {
        const idxA = departmentOrder.indexOf(a.department)
        const idxB = departmentOrder.indexOf(b.department)
        return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB)
      })
    }
    return groups
  }, [filteredMembers, departmentOrder])
  const toggleDropdown = (dropdown: string) => {
    setOpenDropdown(openDropdown === dropdown ? null : dropdown)
  }

  return (
    <section className="pt-24 md:pt-32 pb-12 md:pb-20 px-6 md:px-12 bg-stone">
      <div className="max-w-380 mx-auto">
        {/* Hero Header */}
        {(heading || content) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start mb-12 lg:mb-16">
            {/* Left Column - Heading */}
            <div>
              {heading && (
                <RichText
                  data={heading}
                  className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-medium leading-tight text-brand-blue [&_em]:font-serif [&_span]:italic [&_span]:text-brand-gold"
                />
              )}
            </div>

            {/* Right Column - Content */}
            <div>
              {content && (
                <RichText
                  data={content}
                  className="prose prose-sm max-w-none text-gray-700 leading-relaxed [&_p]:mb-4 [&_p]:text-sm [&_p]:md:text-base"
                />
              )}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="mb-12">
          {/* Search Bar */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Who are you looking for?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-16 py-3 border border-gray-300 bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
            />
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap gap-4">
            {/* Title Filter */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('title')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full hover:border-gray-400 transition-colors"
              >
                <span className="text-sm text-gray-600">
                  Title:{' '}
                  <span className="text-gray-900 font-medium">
                    {selectedTitle === 'all' ? 'All' : selectedTitle}
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-600 transition-transform ${openDropdown === 'title' ? 'rotate-180' : ''}`}
                />
              </button>
              {openDropdown === 'title' && (
                <div className="absolute top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedTitle('all')
                      setOpenDropdown(null)
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                  >
                    All
                  </button>
                  {titles.map((title) => (
                    <button
                      key={title}
                      onClick={() => {
                        setSelectedTitle(title)
                        setOpenDropdown(null)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                    >
                      {title.charAt(0).toUpperCase() + title.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Department Filter */}
            <div className="relative">
              <button
                onClick={() => toggleDropdown('department')}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full hover:border-gray-400 transition-colors"
              >
                <span className="text-sm text-gray-600">
                  Department:{' '}
                  <span className="text-gray-900 font-medium">
                    {selectedDepartment === 'all'
                      ? 'All'
                      : formatDepartmentLabel(selectedDepartment)}
                  </span>
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-gray-600 transition-transform ${openDropdown === 'department' ? 'rotate-180' : ''}`}
                />
              </button>
              {openDropdown === 'department' && (
                <div className="absolute top-full mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedDepartment('all')
                      setOpenDropdown(null)
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                  >
                    All
                  </button>
                  {departments.map((dept) => (
                    <button
                      key={dept}
                      onClick={() => {
                        setSelectedDepartment(dept || 'all')
                        setOpenDropdown(null)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                    >
                      {dept && formatDepartmentLabel(dept)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Team Grid - Grouped by Department */}
        {groupedByDepartment.map((group, groupIndex) => (
          <div key={group.department}>
            {/* Department Divider */}
            <div className={`flex items-center gap-4 ${groupIndex > 0 ? 'mt-16' : ''} mb-8`}>
              <h2 className="text-lg font-semibold text-gray-900 whitespace-nowrap">
                {formatDepartmentLabel(group.department)}
              </h2>
              <div className="flex-1 h-px bg-gray-300" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
              {group.members.map((member) => {
                const photo = member.photo as Media | null | undefined

                return (
                  <Link
                    key={member.id}
                    href={`/team/${member.slug}`}
                    className="group cursor-pointer"
                  >
                    {/* Photo */}
                    {getMediaUrl(photo, 'card') && (
                      <div className="relative aspect-square rounded-xl overflow-hidden w-full mb-5">
                        <Image
                          src={getMediaUrl(photo, 'card')}
                          alt={member.name || ''}
                          fill
                          className="object-cover object-top group-hover:scale-105 transition-transform duration-500 bg-gray-300"
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div>
                      {/* Name */}
                      <h3 className="text-xl md:text-2xl font-medium text-gray-900 group-hover:text-brand-blue transition-colors">
                        {member.name}
                      </h3>

                      {/* Title */}
                      {member.title && (
                        <p className="text-base text-gray-600 mb-2">{member.title}</p>
                      )}

                      {/* View Profile Link */}
                      <div className="inline-flex items-center gap-2 text-sm font-semibold text-brand-gold group-hover:text-brand-blue transition-colors">
                        View Profile
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        ))}

        {/* No Results */}
        {filteredMembers.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No team members found matching your criteria.</p>
          </div>
        )}
      </div>
    </section>
  )
}
